import { useCallback, useMemo, useRef, useState } from 'react'
import axios from '@fs/zion-axios'
import { getAnonSession, getSessionId } from '@fs/zion-session'
import { useStatusOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@fs/zion-analytics'
import { clearCachedUser } from '@fs/zion-user'
import { useFeatureFlag } from '@fs/zion-flags'
import useRequestState from '../useRequestState'
import useCenterOnboarding from './useCenterOnboarding'
import setCookie from '../../helpers/cookie'
import { updateFSSessionId } from '../../helpers/sessionCleanup'
import { getSessionStorageItem, removeSessionStorageItem, setSessionStorageItem } from '../../helpers/sessionStorage'
import { putExperienceBlob } from '../../helpers/experienceBlob'
import {
  getOnboardingAnswersCountry,
  onboardingAnswersToGuestProfile,
  extractOnboardingAnswers,
  guestProfileToOnboardingAnswers,
} from '../../helpers/guestExperience'

const axiosInstance = axios.create()

const commonServiceBase = '/service/discovery/common'
const inCenterServiceBase = '/service/discovery/incenterexperience'
const DICE_ACTIVATION_KEY = 'diceCenterSession.activation'

// The DICE redemption on this kiosk, `{ centerCode, code }`, or null before one
export const getDiceActivation = () => getSessionStorageItem(DICE_ACTIVATION_KEY) ?? null

// True once a center code has been redeemed on this kiosk against DICE
export const hasDiceActivation = () => Boolean(getDiceActivation())

const restoreKeypadSession = async (previousSessionId, adoptedSessionId) => {
  clearCachedUser()

  if (previousSessionId && previousSessionId !== adoptedSessionId) {
    updateFSSessionId(previousSessionId)
    return
  }

  updateFSSessionId('')
  try {
    await getAnonSession()
  } catch {
    // A guarded route can retry.
  }
}

// A failure after the code's DICE session was adopted hands the keypad back its own session
const restoreAfterFailedRedemption = async (diceSession) => {
  if (diceSession?.adoptedSessionId) {
    await restoreKeypadSession(diceSession.previousSessionId, diceSession.adoptedSessionId)
  }
}

const isInvalidCodeError = (error) => [401, 404].includes(error?.response?.status)

// A code the patron can retype: DICE answers 401/404 for one, the legacy service 401
const isActionableCodeError = (error, diceEnabled) =>
  diceEnabled ? isInvalidCodeError(error) : error?.response?.status === 401

const rememberDiceActivation = (center, code) =>
  setSessionStorageItem({
    [DICE_ACTIVATION_KEY]: { centerCode: center, code },
  })

const fetchExperienceAnswers = async () =>
  extractOnboardingAnswers(await axiosInstance.get(`${inCenterServiceBase}/experience`))

// We are manually calling the platform endpoint here instead of using getUser() because
// getUser() relies on the zion-session isLoggedIn check which may not be updated yet
// after switching the session ID.
const fetchCurrentUser = async () => {
  const userResponse = await axiosInstance.get('/platform/users/current')
  const user = userResponse.data.users[0]
  const birthYear = user.birthDate ? user.birthDate.substring(0, 4) : undefined
  return { ...user, birthYear, surname: user.familyName }
}

// Looks up the DICE session behind a code. previousSessionId is the keypad's own
// session, so a failure after the code's session is adopted can restore it.
const lookupDiceSession = async (center, code) => {
  removeSessionStorageItem(DICE_ACTIVATION_KEY)
  const previousSessionId = getSessionId()
  const response = await axiosInstance.get(`${inCenterServiceBase}/sessions/lookup?centerCode=${center}&code=${code}`)
  const { sessionId: adoptedSessionId, isGuestUser, language } = response.data
  return { previousSessionId, adoptedSessionId, isGuestUser, language }
}

// DICE stores guest details in the experience blob.
const resolveDiceGuest = async (language) => {
  let guestProfile = {}
  try {
    guestProfile = onboardingAnswersToGuestProfile(await fetchExperienceAnswers())
  } catch (error) {
    if (error?.response?.status !== 404) {
      throw error
    }
  }
  return { ...guestProfile, guestUser: true, language }
}

const resolveDiceSignedInUser = async (language) => {
  // getUser() may still reflect the previous session.
  const user = await fetchCurrentUser()

  // DSC-10398: the onboarding country step's choice overrides the account country.
  // Best effort - an unreadable blob must never block a signed-in patron.
  let onboardingCountry
  try {
    onboardingCountry = getOnboardingAnswersCountry(await fetchExperienceAnswers())
  } catch {
    onboardingCountry = undefined
  }

  return { ...user, language, onboardingCountry }
}

// Adopts the code's DICE session, then resolves its patron and records the redemption
const resolveDiceUser = async (center, code, { adoptedSessionId, isGuestUser, language }) => {
  clearCachedUser()
  updateFSSessionId(adoptedSessionId)

  const user = isGuestUser ? await resolveDiceGuest(language) : await resolveDiceSignedInUser(language)
  rememberDiceActivation(center, code)
  return user
}

const resolveLegacyUser = async (center, code) => {
  const lookupEndpoint = `${commonServiceBase}/discovery-center/sessions/lookup`
  const lookupParams = `?centerCode=${center}&code=${code}`
  const lookupURL = `${lookupEndpoint}${lookupParams}`

  const response = await axiosInstance.get(lookupURL)
  clearCachedUser()
  updateFSSessionId(response.data.fsSessionId)

  if (response.data.guestUser) {
    return response
  }

  const user = await fetchCurrentUser()
  return { ...user, language: response.data.language }
}

// The discovery_discoveryReact_diceCenterSessions flag selects which service owns
// center sessions on this device (DSC-10083): ON = DICE (the support pages create new
// centers only there), OFF = the legacy common service, byte-for-byte master behavior —
// the quick-rollback path if DICE misbehaves. The onboarding flow is DICE-native (it
// mints its sessions on DICE directly), so onboarding (see useCenterOnboarding) forces DICE regardless:
// codes minted by one service can't be resolved by the other. Either way, a center's
// sign-in device and its kiosks must be flipped together.
const useDiceCenterService = () => {
  const { isOn: diceSessionsOn } = useFeatureFlag('discovery_discoveryReact_diceCenterSessions')
  const onboardingOn = useCenterOnboarding()
  return diceSessionsOn || onboardingOn
}

export const cleanupPostLogout = () => {
  setCookie('fssessionid', '') // Don't use null!
  setCookie('fslanguage', '')
  sessionStorage.clear()
}

export const getFilteredCenters = ({ allowedCenters, centers }) => {
  const filteredCenters = centers.filter((testCenter) => {
    const match = allowedCenters.find((testAllowedCenter) => testAllowedCenter === testCenter.name)
    return !!match
  })
  return filteredCenters
}

export const useLogout = () => {
  const diceEnabled = useDiceCenterService()

  const logout = useCallback(async () => {
    // Delete by code because the user-wide sweep can 422 on retained sessions (DSC-9531).
    if (diceEnabled) {
      try {
        const activation = getSessionStorageItem(DICE_ACTIVATION_KEY)
        if (activation?.centerCode && activation?.code) {
          await axios.delete(`${inCenterServiceBase}/sessions`, {
            params: { centerCode: activation.centerCode, code: activation.code },
          })
        } else {
          await axios.delete(`${inCenterServiceBase}/sessions/user`)
        }
      } catch {
        // Intentionally swallowed — logout must always proceed to end the platform session.
      }
    }

    const sessionId = getSessionId()
    const url = `/service/ident/session/sessions/${sessionId}`
    return axios.delete(url, {
      headers: {
        Accept: 'application/session-v1+json', // Won't work without this header
      },
    })
  }, [diceEnabled])

  return useRequestState(logout)
}

export const useGetUserFromCode = () => {
  const { t } = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const diceEnabled = useDiceCenterService()
  // DSC-9577: tracked as a boolean rather than a message so the copy is translated at render
  // time and follows a mid-session language change.
  const [hasInvalidCode, setHasInvalidCode] = useState(false)
  const activeAttemptRef = useRef(null)

  const clearInvalidCode = useCallback(() => setHasInvalidCode(false), [])

  const fetchUserByCode = useCallback(
    ({ center, code }) => {
      if (activeAttemptRef.current) return activeAttemptRef.current

      const attempt = (async () => {
        setHasInvalidCode(false)
        let diceSession
        try {
          trackEvent({ event_name: `getUserFromCode_${center}:GO_BUTTON:CLICK`, center, code })

          if (!diceEnabled) return await resolveLegacyUser(center, code)

          diceSession = await lookupDiceSession(center, code)
          return await resolveDiceUser(center, code, diceSession)
        } catch (error) {
          await restoreAfterFailedRedemption(diceSession)

          // Only actionable code failures belong on the keypad (DSC-9577).
          if (isActionableCodeError(error, diceEnabled)) {
            setHasInvalidCode(true)
            return null
          }

          showStatusOverlay({
            message: t('aam.add-year.error', 'An error has occurred'),
            type: 'error',
          })
        }
        return null
      })()

      const singleFlightAttempt = attempt.finally(() => {
        if (activeAttemptRef.current === singleFlightAttempt) activeAttemptRef.current = null
      })
      activeAttemptRef.current = singleFlightAttempt
      return singleFlightAttempt
    },
    [showStatusOverlay, t, diceEnabled]
  )

  const [fetchUser, requestState] = useRequestState(fetchUserByCode)

  const codeError = useMemo(
    () => ({
      message: hasInvalidCode ? t('code-entry.invalid-code', 'Invalid code. Please try again.') : null,
      clear: clearInvalidCode,
    }),
    [hasInvalidCode, t, clearInvalidCode]
  )

  return [fetchUser, requestState, codeError]
}

export const useGetCode = (center) => {
  const diceEnabled = useDiceCenterService()

  const getCode = useCallback(
    async (guestProfile) => {
      if (!diceEnabled) {
        // Legacy common-service mint — master behavior, guest details ride the POST body.
        return axios.post(`${commonServiceBase}/discovery-center/sessions?centerCode=${center}`, guestProfile)
      }

      const mintSession = () => axios.post(`${inCenterServiceBase}/sessions`, null, { params: { centerCode: center } })

      let response
      try {
        response = await mintSession()
      } catch (error) {
        // 409 = this identity already has an active session (POST is deliberately not
        // idempotent; the service says resume, don't retry).
        if (error?.response?.status !== 409) throw error

        const recovered = await axios.get(`${inCenterServiceBase}/sessions/user`)
        const recoveredCenter = recovered.data?.centerCode
        // The service resolves center codes case-insensitively and returns canonical casing.
        if (!recoveredCenter || recoveredCenter.toLowerCase() === center?.toLowerCase()) {
          response = recovered
        } else {
          // A session at another center hands out a code this center's kiosks can't
          // resolve. End it by code, at ITS center — DELETE /sessions/user sweeps
          // completed sessions too and 422s whenever one sits in retention (DSC-9531) —
          // then mint a fresh one here.
          await axios.delete(`${inCenterServiceBase}/sessions`, {
            params: { centerCode: recoveredCenter, code: recovered.data.code },
          })
          response = await mintSession()
        }
      }

      // The guest details the legacy login form collects ride the common-service session
      // create; under DICE they live in the experience blob, where the kiosk lookup
      // rebuilds the profile from. Best-effort: a code with no saved name still works —
      // the guest is just prompted for details downstream.
      const onboardingAnswers = guestProfileToOnboardingAnswers(guestProfile)
      if (Object.keys(onboardingAnswers).length) {
        try {
          await putExperienceBlob({ onboardingAnswers })
        } catch {
          // Intentionally swallowed — never block handing out a working code.
        }
      }

      return response
    },
    [center, diceEnabled]
  )

  return useRequestState(getCode)
}
